import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canEdit, canDelete } from "../common/permissionUtils";
import Back from "../../assets/svg/back.svg";
import Profile from "../../assets/png/profile.png";
import Mail from "../../assets/svg/mail.svg";
import Phone from "../../assets/svg/phone.svg";
import {
  getContactDataApi,
  getActivityApi,
  getHistoryApi,
} from "./contacts-api";
import homeAddress from "../../assets/svg/home-address.svg";
import { FaPlus } from "react-icons/fa";
import { ReactComponent as ClockIc } from "../../assets/svg/clock_icsa.svg";
import { ReactComponent as DownArrowIc } from "../../assets/svg/downArrowIc.svg";
import { ReactComponent as NoteHist } from "../../assets/svg/note_hist.svg";
import { ReactComponent as LeadHist } from "../../assets/svg/lead_hist.svg";
import { ReactComponent as AttachHist } from "../../assets/svg/attach_hist.svg";
import { ReactComponent as StatusHist } from "../../assets/svg/status_hist.svg";
import { ReactComponent as QuotHist } from "../../assets/svg/quot_hist.svg";
import { ReactComponent as EditHist } from "../../assets/svg/edit_hist.svg";
import { ReactComponent as ContactHist } from "../../assets/svg/contact_hist.svg";
import { ReactComponent as EmailHist } from "../../assets/svg/email_hist.svg";
import { ReactComponent as CallHist } from "../../assets/svg/call_hist.svg";
import { sanitizeInput, validateUrl } from "../common/security-utils.js";
import "./contacts-view.css";

const ContactsView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Contacts");
  const hasDeletePermission = canDelete(rolePermissionList, "Contacts");

  const [overview, setOverview] = useState({});
  const [activitiesList, setActivitiesList] = useState([]);
  const [historyList, setHistoryList] = useState([]);
  const [activeTab, setActiveTab] = useState("Contact Details");
  const [activityTab, setActivityTab] = useState("All");
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const historyIcons = {
    note: <NoteHist />,
    lead: <LeadHist />,
    attachment: <AttachHist />,
    status: <StatusHist />,
    quotation: <QuotHist />,
    edit: <EditHist />,
    contact: <ContactHist />,
    email: <EmailHist />,
    call: <CallHist />,
  };

  const getCleanType = (type) => {
    if (!type) return "";
    return type.trim().toLowerCase().split(" ")[0];
  };

  // VAPT: Sanitization helper for safe text display
  const sanitize = (text) => {
    if (!text) return "";
    return DOMPurify.sanitize(sanitizeInput(String(text)), {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
  };

  // VAPT: Validate and sanitize URL for external links
  const getSafeMapUrl = (address, area, city, state, country, postalcode) => {
    const addressParts = [
      address || "",
      area || "",
      city || "",
      state || "",
      country || "",
      postalcode || "",
    ]
      .map((part) => sanitize(part))
      .filter(Boolean)
      .join(" ")
      .trim();

    if (!addressParts) return null;

    const baseUrl = "https://www.google.com/maps/search/?api=1&query=";
    const encodedQuery = encodeURIComponent(addressParts);
    const fullUrl = baseUrl + encodedQuery;

    // Validate URL format
    return validateUrl(fullUrl) ? fullUrl : null;
  };

  // Helper functions to reduce code duplication
  const EmptyState = ({ message }) => (
    <div style={styles.emptyState}>{message}</div>
  );

  const SectionTitle = ({ children }) => (
    <h5 style={styles.sectionTitle}>{children}</h5>
  );

  const DetailRow = ({ label, value }) => (
    <div className="detail-row">
      <span className={label === "Department" ? "sub-title" : ""}>{label}</span>
      <span className="sub-title-value">{sanitize(value) || "-"}</span>
    </div>
  );

  // Reusable styles
  const styles = {
    emptyState: {
      textAlign: "center",
      padding: "40px",
      color: "#999",
      fontSize: "14px",
    },
    sectionTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#1E1E1E",
      marginBottom: "16px",
    },
    cardSection: {
      marginTop: "16px",
    },
    pillContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: "12px",
    },
  };

  const getActivity = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getActivityApi(id);
      if (res.status === 200) {
        setActivitiesList(res.data.activities || []);
      }
    } catch (err) {
      // Error handling without exposing internal details
    }
  }, [id]);

  const getHistory = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getHistoryApi(id);
      if (res.status === 200) {
        setHistoryList(res.data.histories || []);
      }
    } catch (err) {
      // Error handling without exposing internal details
    }
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const res = await getContactDataApi(id);
        if (res.status === 200) {
          setOverview(res.data.contact || {});
          // If backend returns activities, set them; otherwise keep empty
          /*  setActivities(res.data.activities || []); */
        }
      } catch (err) {
        // Error handling without exposing internal details
      }
    };

    fetchData();
    getActivity();
    getHistory();
  }, [id, getActivity, getHistory]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderContactInfo = () => {
    const contact = overview;
    return (
      <div className="contact-view-card">
        <div className="info-section">
          <div className="section-title">Contact information</div>

          <div className="row mt-2 info-list">
            {(contact.emails || []).map((e, i) => {
              const emailVal = sanitize(e.email || "");
              const emailType = sanitize(e.type || "");
              const isPrimary = emailType.toLowerCase() === "primary";
              return (
                <div
                  key={i}
                  className="col-12 info-item d-flex align-items-center"
                >
                  <img src={Mail} className="row-icon" alt="mail" />
                  <div className="text ellipsis">{emailVal}</div>
                  <span className="ellipse" />
                  <div
                    className={`meta pill ${isPrimary ? "primary-pill" : ""}`}
                  >
                    {emailType}
                  </div>
                </div>
              );
            })}

            {(contact.mobiles || []).map((m, i) => {
              const phoneVal = sanitize(m.mobile_number || "");
              const phoneType = sanitize(m.type || "");
              const isPrimary = phoneType.toLowerCase() === "primary";
              return (
                <div
                  key={i}
                  className="col-12 mb-1 info-item d-flex align-items-center"
                >
                  <img src={Phone} className="row-icon" alt="phone" />
                  <div className="text ellipsis">{phoneVal}</div>
                  <div
                    className={`meta pill ${isPrimary ? "primary-pill" : ""}`}
                  >
                    {phoneType}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="divider"></div>
          <div className="mt-3">
            <div className="section-title">Other information</div>
            <DetailRow label="Department" value={contact.department} />
            <DetailRow label="Source" value={contact.lead_source} />
            <DetailRow label="Industry type" value={contact.industrial_type} />
            <DetailRow label="Designation" value={contact.designation} />
           {/*  {(contact.account_ids || []).length > 0 && (
              <DetailRow label="Accounts" value={(contact.account_ids || []).map((a) => a.label).join(", ")} />
            )}
            {(contact.lead_ids || []).length > 0 && (
              <DetailRow label="Leads" value={(contact.lead_ids || []).map((l) => l.label).join(", ")} />
            )} */}
          </div>

          <div className="mt-3">
            <div className="section-title">Description</div>
            <div className="desc-value">
              {sanitize(contact.description) || "-"}
            </div>
          </div>

          <div className="mt-3">
            <div className="section-title">Created At</div>
            <div className="desc-value">
              {sanitize(contact.created_at) || "-"}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="contacts-view-page">
      <div className="view-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-3">
          <img
            src={Back}
            alt="back"
            className="back-icon"
            onClick={() => navigate(-1)}
          />
          <div className="info-header d-flex align-items-center gap-2">
            <div className="profile-sm">
              {overview.profile ? (
                <img src={overview.profile} alt="profile" />
              ) : (
                <img src={Profile} alt="profile" />
              )}
            </div>
            <div>
              <div className="name">
                {`${sanitize(overview.first_name) || ""} ${
                  sanitize(overview.last_name) || ""
                }`.trim() || "Contact"}
              </div>
              <div className="sub muted">
                {sanitize(overview.designation) || "-"}
                {overview.designation && overview.companyname && (
                  <span className="ellipse" />
                )}
                {overview.companyname && (
                  <span className="pill">{sanitize(overview.companyname)}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="view-actions d-flex align-items-center gap-2">
          {/*  <Button className="btn-send">Send mail</Button> */}
          <div
            className="fs-14 fw-600 text-white d-flex justify-content-center align-items-center"
            style={{
              cursor: "pointer",
              width: "82px",
              height: "36px",
              borderRadius: "100px",
              border: "1px solid #D4DDE7",
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigate("/contacts/create", { state: { id: id } });
            }}
          >
            <span
              style={{ fontSize: "14px", fontWeight: "500", color: "black" }}
            >
              Edit
            </span>
          </div>
          {/* <button className="icon-circle" aria-label="menu">
            <img src={ThreeDot} alt="menu" />
          </button> */}
        </div>
      </div>

      <div className="row mx-0">
        <div md={4} className="col-md-4 left-panel">
          {renderContactInfo()}
        </div>

        <div md={8} className="col-md-8 mt-3 px-2">
          <div className="">
            <div className="tabs d-flex align-items-center justify-content-between">
              <div className="tab-list">
                {["Contact Details", "Accounts", "Leads", "Activities", "History"].map((t) => (
                  <button
                    key={t}
                    className={`tab-btn ${activeTab === t ? "active" : ""}`}
                    onClick={() => setActiveTab(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {/*  <div className="tab-actions">
                <Button variant="outline-primary">+ Add</Button>
              </div> */}
            </div>

            <div className="tab-content mt-3">
              {activeTab === "Contact Details" && (
                <div className=" ">
                  {/* Social Media Section */}
                  {overview.social_links &&
                    overview.social_links.length > 0 && (
                      <div className="right-card detail-section">
                        <SectionTitle>Social Media</SectionTitle>
                        <div style={styles.pillContainer}>
                          {overview.social_links.map((social, idx) => {
                            // Determine icon based on platform name
                            const platformName = sanitize(
                              social.name?.toLowerCase() || "",
                            );
                            let bgColor = "#E8F4FD";
                            let iconColor = "#1DA1F2";

                            if (platformName.includes("instagram")) {
                              bgColor = "#FCE8F3";
                              iconColor = "#E1306C";
                            } else if (platformName.includes("facebook")) {
                              bgColor = "#E8F1FD";
                              iconColor = "#1877F2";
                            } else if (
                              platformName.includes("twitter") ||
                              platformName.includes("x")
                            ) {
                              bgColor = "#E8E8E8";
                              iconColor = "#000000";
                            } else if (platformName.includes("linkedin")) {
                              bgColor = "#E8F1FD";
                              iconColor = "#0A66C2";
                            }

                            // VAPT: Sanitize and validate social media link
                            const socialLink = sanitize(social.link || "");
                            const displayName = sanitize(social.name || "S");

                            return (
                              <div
                                key={idx}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  padding: "8px 12px",
                                  backgroundColor: bgColor,
                                  borderRadius: "16px",
                                  fontSize: "13px",
                                  color: "#1E1E1E",
                                  fontWeight: "400",
                                }}
                              >
                                <div
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "50%",
                                    backgroundColor: iconColor,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "12px",
                                  }}
                                >
                                  {displayName?.charAt(0).toUpperCase() || "S"}
                                </div>
                                <span style={{ fontWeight: "400" }}>
                                  {socialLink
                                    ?.replace(/^https?:\/\/(www\.)?/, "")
                                    .split("/")[0] || displayName}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {/* Tags Section */}
                  {overview.tags && overview.tags.length > 0 && (
                    <div
                      className="right-card detail-section"
                      style={styles.cardSection}
                    >
                      <SectionTitle>Tags</SectionTitle>
                      <div style={{ ...styles.pillContainer, gap: "16px" }}>
                        {overview.tags.map((tagObj, idx) => (
                          <span
                            key={idx}
                            style={{
                              padding: "4px 12px",
                              backgroundColor: "#F3F3F3",
                              borderRadius: "8px",
                              fontSize: "13px",
                              color: "#222326",
                              fontWeight: "500",
                            }}
                          >
                            {sanitize(tagObj.tag)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes Section */}
                  {overview.notes && (
                    <div
                      className="right-card detail-section"
                      style={{ marginTop: "16px" }}
                    >
                      <h5
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#172B4D",
                          marginBottom: "16px",
                        }}
                      >
                        Notes
                      </h5>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#21272C",
                          lineHeight: "1.6",
                          fontWeight: "400",
                        }}
                      >
                        {sanitize(overview.notes)}
                      </div>
                    </div>
                  )}

                  {/* Address Information Section */}
                  {(overview.address ||
                    overview.city ||
                    overview.state ||
                    overview.country) && (
                    <div
                      className="right-card detail-section"
                      style={{ marginTop: "16px" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "16px",
                        }}
                      >
                        <h5
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#21272C",
                            margin: 0,
                          }}
                        >
                          Address Information
                        </h5>
                        <a
                          href={
                            getSafeMapUrl(
                              overview.address,
                              overview.area,
                              overview.city,
                              overview.state,
                              overview.country,
                              overview.postalcode,
                            ) || "#"
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            // VAPT: Prevent navigation if URL is invalid
                            if (
                              !getSafeMapUrl(
                                overview.address,
                                overview.area,
                                overview.city,
                                overview.state,
                                overview.country,
                                overview.postalcode,
                              )
                            ) {
                              e.preventDefault();
                            }
                          }}
                          style={{
                            fontSize: "14px",
                            color: "#3D4AF5",
                            textDecoration: "none",
                            fontWeight: "500",
                          }}
                        >
                          Locate in Map
                        </a>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "12px",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            backgroundColor: "#FFE8E8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <img src={homeAddress} alt="home address" />
                        </div>

                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#666666",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {sanitize(overview.address_title) || "HOME ADDRESS"}
                        </div>
                      </div>
                      <div
                        className="mt-2"
                        style={{
                          fontSize: "14px",
                          color: "#28303F",
                          lineHeight: "1.6",
                          fontWeight: "400",
                        }}
                      >
                        {[
                          sanitize(overview.address),
                          sanitize(overview.area),
                          sanitize(overview.city),
                          sanitize(overview.state),
                          sanitize(overview.country),
                          sanitize(overview.postalcode),
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    </div>
                  )}

                  {/* Empty state if no data */}
                  {!overview.social_links?.length &&
                    !overview.tags?.length &&
                    !overview.notes &&
                    !overview.address &&
                    !overview.city && (
                      <EmptyState message="No contact details available" />
                    )}
                </div>
              )}

              {activeTab === "Accounts" && (
                <div className="right-card detail-section" style={{ width: "100%" }}>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#172B4D",
                      marginBottom: "16px",
                    }}
                  >
                    Accounts - {(overview.account_ids || []).length}
                  </div>
                  {(overview.account_ids || []).length > 0 ? (
                    (overview.account_ids || []).map((acc, idx) => (
                      <div
                        key={acc.value ?? idx}
                        onClick={() =>
                          navigate("/accounts/view", { state: { id: acc.value } })
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "14px 16px",
                          borderRadius: "12px",
                          border: "1px solid #EBEBEB",
                          marginBottom: "10px",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ fontSize: "14px", fontWeight: "500", color: "#3D4AF5" }}>
                          {acc.code ? `${sanitize(acc.code)} - ${sanitize(acc.label)}` : sanitize(acc.label)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <EmptyState message="No accounts mapped" />
                  )}
                </div>
              )}

              {activeTab === "Leads" && (
                <div className="right-card detail-section" style={{ width: "100%" }}>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#172B4D",
                      marginBottom: "16px",
                    }}
                  >
                    Leads - {(overview.lead_ids || []).length}
                  </div>
                  {(overview.lead_ids || []).length > 0 ? (
                    (overview.lead_ids || []).map((lead, idx) => (
                      <div
                        key={lead.value ?? idx}
                        onClick={() =>
                          navigate("/leads/view", { state: { id: lead.value } })
                        }
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "14px 16px",
                          borderRadius: "12px",
                          border: "1px solid #EBEBEB",
                          marginBottom: "10px",
                          cursor: "pointer",
                        }}
                      >
                        <span style={{ fontSize: "14px", fontWeight: "500", color: "#3D4AF5" }}>
                          {lead.code ? `${sanitize(lead.code)} - ${sanitize(lead.label)}` : sanitize(lead.label)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <EmptyState message="No leads mapped" />
                  )}
                </div>
              )}

              {activeTab === "Activities" && (
                <div
                  className="right-card detail-section"
                  style={{ width: "100%" }}
                >
                  {/* Header with Add Button */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#172B4D",
                      }}
                    >
                      Activities
                    </div>
                    <div style={{ position: "relative" }} ref={dropdownRef}>
                      {/* Button */}
                      <button
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          backgroundColor: "#ffffff",
                          borderRadius: "40px",
                          padding: "6px 14px",
                          border: "1px solid #E5E7EB",
                          cursor: "pointer",
                          fontSize: "14px",
                          color: "#3D4AF5",
                          fontWeight: "600",
                        }}
                        onClick={() => setOpen(!open)}
                      >
                        <FaPlus size={12} color="#3D4AF5" /> Add <DownArrowIc />
                      </button>

                      {/* Dropdown Menu */}
                      {open && (
                        <div
                          style={{
                            position: "absolute",
                            top: "44px",
                            right: 0,
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                            border: "1px solid #E5E7EB",
                            boxShadow: "0px 4px 15px rgba(0,0,0,0.05)",
                            minWidth: "150px",
                            zIndex: 10,
                            padding: "6px 0",
                          }}
                        >
                          <div
                            style={{
                              padding: "10px 14px",
                              fontSize: "14px",
                              cursor: "pointer",
                              color: "#172B4D",
                            }}
                            onClick={() => {
                              navigate("/calls/create", {
                                state: {
                                  callFrom: "Contact",
                                  moduleDetail: {
                                    value: id,
                                    label:
                                      `${sanitize(overview.first_name) || ""} ${
                                        sanitize(overview.last_name) || ""
                                      }`.trim(),
                                  },
                                },
                              });
                              setOpen(false);
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.background = "#F5F6FF")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.background = "transparent")
                            }
                          >
                            Call
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Filters Row */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "16px",
                    }}
                  >
                    {["All", "Calls", "Email"].map((f) => (
                      <div
                        key={f}
                        onClick={() => setActivityTab(f)}
                        style={{
                          padding: "6px 16px",
                          borderRadius: "8px",
                          backgroundColor:
                            activityTab === f ? "#3D4AF5" : "#F3F3F3",
                          fontWeight: "500",
                          fontSize: "13px",
                          color: activityTab === f ? "#ffffff" : "#172B4D",
                          cursor: "pointer",
                        }}
                      >
                        {f}
                      </div>
                    ))}
                  </div>

                  {/* CALL CARDS */}
                  {activitiesList && activitiesList.length > 0 ? (
                    activitiesList.map((activity, index) => (
                      <div
                        key={activity.id || index}
                        style={{
                          background: "#FFFFFF",
                          borderRadius: "16px",
                          padding: "18px",
                          marginBottom: "12px",
                          border: "1px solid #EBEBEB",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: "15px",
                              color: "#3D4AF5",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "80%",
                            }}
                          >
                            {sanitize(activity.tag1)} with{" "}
                            {sanitize(activity.tag2)}
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "13px",
                            marginTop: "10px",
                            color: "#172B4D",
                          }}
                        >
                          <ClockIc />
                          <span>{sanitize(activity.date)}</span>
                        </div>

                        <div
                          style={{
                            fontSize: "13px",
                            color: "#8790A1",
                            marginTop: "4px",
                          }}
                        >
                          {sanitize(activity.description) || "-"}
                        </div>
                      </div>
                    ))
                  ) : (
                    <EmptyState message="No activities found" />
                  )}
                </div>
              )}

              {activeTab === "History" &&
                (() => {
                  let formattedDate = "";
                  if (historyList.length > 0 && historyList[0].date) {
                    const dateObj = new Date(historyList[0].date);
                    formattedDate = dateObj.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    });
                  }
                  return (
                    <div className="right-card detail-section">
                      <h5 className="fw-semibold mb-1">History</h5>
                      <div className="py-4" style={{ overflowX: "auto" }}>
                        <div style={{ overflowX: "auto" }}>
                          <div style={{ minWidth: "300px" }}>
                            {formattedDate && (
                              <div className="position-relative text-center mb-4">
                                <hr />
                                <span
                                  className="position-absolute top-50 start-50 translate-middle bg-white px-4 py-1 text-muted small border rounded-pill"
                                  style={{ fontWeight: 500, fontSize: "15px" }}
                                >
                                  {formattedDate}
                                </span>
                              </div>
                            )}

                            {historyList && historyList.length > 0 ? (
                              historyList.map((HistoryItem, index) => {
                                const iconKey = getCleanType(HistoryItem.type);
                                const Icon =
                                  historyIcons[iconKey] || historyIcons["note"];

                                return (
                                  <div
                                    key={index}
                                    className="d-flex align-items-start"
                                    style={{ marginBottom: "24px" }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "start",
                                        marginRight: "1rem",
                                        position: "relative",
                                      }}
                                    >
                                      <div
                                        className="small text-muted mb-1"
                                        style={{
                                          marginRight: "1rem",
                                          minWidth: "60px",
                                        }}
                                      >
                                        {sanitize(HistoryItem.time)}
                                      </div>

                                      <div
                                        style={{
                                          position: "relative",
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                        }}
                                      >
                                        <div
                                          style={{
                                            backgroundColor: "#f8f9fa",
                                            border: "1px solid #dee2e6",
                                            borderRadius: "50%",
                                            width: "30px",
                                            height: "30px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            zIndex: 1,
                                          }}
                                        >
                                          {Icon}
                                        </div>

                                        {index !== historyList.length - 1 && (
                                          <div
                                            style={{
                                              width: "2px",
                                              height: "100px",
                                              border: "1px dashed #D3D8E0",
                                            }}
                                          />
                                        )}
                                      </div>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                      <p className="mb-1">
                                        <strong
                                          style={{
                                            fontWeight: 500,
                                            fontSize: "16px",
                                            color: "#21272C",
                                          }}
                                        >
                                          {sanitize(HistoryItem.type)}
                                        </strong>
                                        {HistoryItem.link ? (
                                          <span style={{ color: "#3D4AF5" }}>
                                            {" "}
                                            - {sanitize(HistoryItem.link)}
                                          </span>
                                        ) : (
                                          <span
                                            style={{ whiteSpace: "pre-wrap" }}
                                          >
                                            {" "}
                                            -{" "}
                                            {sanitize(HistoryItem.description)}
                                          </span>
                                        )}
                                      </p>
                                      <div className="small text-muted">
                                        By {sanitize(HistoryItem.first_name)} on{" "}
                                        {sanitize(HistoryItem.date)}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <EmptyState message="No history found" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsView;
